import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import { PageHeader, SectionCard, EmptyState } from '../ui';
import { paymentService, type ApiKey, type PartnerWebhook } from '../../services/paymentService';

type Notify = (msg: string, sev: 'success' | 'error') => void;

const fmt = (ms: number | null): string => {
  if (!ms) return '—';
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

const keyState = (k: ApiKey): { label: string; color: 'success' | 'default' | 'error' } =>
  k.revokedAt ? { label: 'đã thu hồi', color: 'error' } : k.enabled ? { label: 'đang bật', color: 'success' } : { label: 'tạm tắt', color: 'default' };

export const ApiKeys: React.FC = () => {
  const [rows, setRows] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [ownerTg, setOwnerTg] = useState('');
  const [label, setLabel] = useState('');
  // Sau khi cấp key: hiện cả API key + webhook token (đều chỉ hiện 1 lần).
  const [issued, setIssued] = useState<{ ownerTg: string; key: string; webhookToken: string } | null>(null);
  const [webhookReloadKey, setWebhookReloadKey] = useState(0);
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' } | null>(null);
  const notify: Notify = (msg, sev) => setToast({ msg, sev });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows((await paymentService.listApiKeys()).keys);
    } catch (e) {
      notify((e as Error).message, 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      notify('Đã copy.', 'success');
    } catch {
      notify('Không copy được — hãy copy thủ công.', 'error');
    }
  };

  // Nhập xong Telegram ID → tự nạp username (fallback tên) vào ô Nhãn.
  const autofillLabel = async () => {
    const tg = ownerTg.trim();
    if (!/^\d{1,20}$/.test(tg)) return;
    try {
      const { user } = await paymentService.getBotUser(tg);
      const name = user?.username || user?.firstName || '';
      if (name) setLabel(name);
    } catch {
      /* im lặng */
    }
  };

  const create = async () => {
    const tg = ownerTg.trim();
    if (!/^\d{1,20}$/.test(tg)) return notify('Telegram ID (ownerTg) phải là chuỗi số.', 'error');
    setBusy(true);
    try {
      const r = await paymentService.createApiKey(tg, label.trim() || undefined);
      setIssued({ ownerTg: tg, key: r.key, webhookToken: r.webhookToken });
      setOwnerTg('');
      setLabel('');
      await load();
      setWebhookReloadKey((k) => k + 1); // webhook row vừa được tạo kèm
    } catch (e) {
      notify((e as Error).message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (k: ApiKey) => {
    setBusy(true);
    try {
      await paymentService.setApiKeyEnabled(k.id, !(k.enabled && !k.revokedAt));
      await load();
    } catch (e) {
      notify((e as Error).message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (k: ApiKey) => {
    if (!window.confirm(`Thu hồi vĩnh viễn key ${k.prefix}… (tg ${k.ownerTg})?\nKhông bật lại được.`)) return;
    setBusy(true);
    try {
      await paymentService.revokeApiKey(k.id);
      notify('Đã thu hồi.', 'success');
      await load();
    } catch (e) {
      notify((e as Error).message, 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <PageHeader title="API Keys" description="Cấp key cho đối tác — mỗi key gắn với một tài khoản Telegram; tính tiền và đọc kết quả theo tài khoản đó. Cấp key sẽ tạo luôn webhook token." />

      <SectionCard title="Cấp key mới">
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
          <TextField
            label="Owner Telegram ID"
            value={ownerTg}
            onChange={(e) => setOwnerTg(e.target.value)}
            onBlur={autofillLabel}
            size="small"
            placeholder="vd 123456789"
            sx={{ maxWidth: 220 }}
          />
          <TextField
            label="Nhãn (tên đối tác)"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            size="small"
            placeholder="tự nạp từ username Telegram"
            sx={{ maxWidth: 260 }}
          />
          <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={create} disabled={busy}>
            Cấp key
          </Button>
        </Stack>

        {issued && (
          <Alert severity="success" sx={{ mt: 2 }} onClose={() => setIssued(null)}>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>
              Cho tg {issued.ownerTg} — chỉ hiện MỘT lần, copy đưa đối tác:
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1.5,
                borderRadius: 1,
                bgcolor: 'action.hover',
                fontFamily: 'monospace',
                fontSize: 13,
              }}
            >
              <Box sx={{ flex: 1, wordBreak: 'break-all' }}>
                <div>API key: {issued.key}</div>
                <div>Webhook token: {issued.webhookToken}</div>
              </Box>
              <Tooltip title="Copy cả 2">
                <IconButton
                  size="small"
                  onClick={() => copy(`API key: ${issued.key}\nWebhook token: ${issued.webhookToken}`)}
                >
                  <ContentCopyRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              LƯU Ý: Đặt URL webhook của khách vào mục “Webhook push kết quả” bên dưới.
            </Typography>
          </Alert>
        )}
      </SectionCard>

      <Box sx={{ mt: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Danh sách API key
          </Typography>
          <Button variant="outlined" startIcon={<RefreshRoundedIcon />} onClick={load} disabled={loading}>
            Tải lại
          </Button>
        </Stack>

        {loading ? (
          <Stack alignItems="center" sx={{ py: 6 }}>
            <CircularProgress />
          </Stack>
        ) : rows.length === 0 ? (
          <EmptyState title="Chưa có key nào" />
        ) : (
          <TableContainer component={Box}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Prefix</TableCell>
                  <TableCell>Owner (tg)</TableCell>
                  <TableCell>Nhãn</TableCell>
                  <TableCell>Trạng thái</TableCell>
                  <TableCell>Tạo lúc</TableCell>
                  <TableCell>Dùng gần nhất</TableCell>
                  <TableCell align="right">Thao tác</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((k) => {
                  const st = keyState(k);
                  return (
                    <TableRow key={k.id}>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{k.prefix}…</TableCell>
                      <TableCell>{k.ownerTg}</TableCell>
                      <TableCell>{k.label || '—'}</TableCell>
                      <TableCell>
                        <Chip size="small" label={st.label} color={st.color} />
                      </TableCell>
                      <TableCell>{fmt(k.createdAt)}</TableCell>
                      <TableCell>{fmt(k.lastUsedAt)}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          {!k.revokedAt && (
                            <Button size="small" variant="outlined" disabled={busy} onClick={() => toggle(k)}>
                              {k.enabled ? 'Tắt' : 'Bật'}
                            </Button>
                          )}
                          {!k.revokedAt && (
                            <Button size="small" variant="outlined" color="error" disabled={busy} onClick={() => revoke(k)}>
                              Thu hồi
                            </Button>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <WebhooksSection notify={notify} copy={copy} reloadKey={webhookReloadKey} />

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.sev} onClose={() => setToast(null)} sx={{ width: '100%' }}>
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

// ---- Webhook: token đã tạo cùng API key; ở đây chỉ đặt URL / xem / xoay token ----
const WebhooksSection: React.FC<{ notify: Notify; copy: (t: string) => void; reloadKey: number }> = ({
  notify,
  copy,
  reloadKey,
}) => {
  const [rows, setRows] = useState<PartnerWebhook[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows((await paymentService.listWebhooks()).webhooks);
    } catch (e) {
      notify((e as Error).message, 'error');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  useEffect(() => {
    load();
  }, [load]);

  const editUrl = async (w: PartnerWebhook) => {
    const url = window.prompt(`URL webhook cho tg ${w.ownerTg} (https://…). Để trống để ngừng đẩy:`, w.url || '');
    if (url === null) return; // bấm Cancel
    const v = url.trim();
    if (v && !/^https:\/\/.+/i.test(v)) return notify('URL phải bắt đầu bằng https://', 'error');
    setBusy(true);
    try {
      await paymentService.setWebhookUrl(w.ownerTg, v);
      notify(v ? 'Đã lưu URL webhook.' : 'Đã xoá URL (ngừng đẩy).', 'success');
      await load();
    } catch (e) {
      notify((e as Error).message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (w: PartnerWebhook) => {
    setBusy(true);
    try {
      await paymentService.setWebhookEnabled(w.ownerTg, !w.enabled);
      await load();
    } catch (e) {
      notify((e as Error).message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const copyToken = async (w: PartnerWebhook) => {
    try {
      const { token } = await paymentService.revealWebhookToken(w.ownerTg);
      await copy(token);
    } catch (e) {
      notify((e as Error).message, 'error');
    }
  };

  const rotate = async (w: PartnerWebhook) => {
    if (!window.confirm(`Xoay token mới cho tg ${w.ownerTg}? Token cũ sẽ ngừng hiệu lực.`)) return;
    setBusy(true);
    try {
      const { token } = await paymentService.rotateWebhookToken(w.ownerTg);
      await copy(token);
      notify('Đã xoay token mới (đã copy) — nhớ cập nhật cho đối tác.', 'success');
    } catch (e) {
      notify((e as Error).message, 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Webhook push kết quả
        </Typography>
        <Button variant="outlined" startIcon={<RefreshRoundedIcon />} onClick={load} disabled={loading}>
          Tải lại
        </Button>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Token tạo sẵn khi cấp API key. Đặt URL (https) để bắt đầu đẩy kết quả. Mỗi POST kèm header{' '}
        <code>Authorization: Apikey &lt;token&gt;</code> — đối tác chỉ cần so khớp token.
      </Typography>

      {loading ? (
        <Stack alignItems="center" sx={{ py: 4 }}>
          <CircularProgress />
        </Stack>
      ) : rows.length === 0 ? (
        <EmptyState title="Chưa có webhook nào (cấp API key để tạo)" />
      ) : (
        <TableContainer component={Box}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Owner (tg)</TableCell>
                <TableCell>URL</TableCell>
                <TableCell>Trạng thái</TableCell>
                <TableCell>Lần giao gần nhất</TableCell>
                <TableCell align="right">Thao tác</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((w) => (
                <TableRow key={w.ownerTg}>
                  <TableCell>{w.ownerTg}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all', maxWidth: 320 }}>
                    {w.url || <span style={{ opacity: 0.5 }}>chưa đặt</span>}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={w.enabled ? 'bật' : 'tắt'} color={w.enabled ? 'success' : 'default'} />
                  </TableCell>
                  <TableCell>{w.lastAt ? `${w.lastStatus ?? '—'} · ${fmt(w.lastAt)}` : '—'}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button size="small" variant="outlined" disabled={busy} onClick={() => editUrl(w)}>
                        Sửa URL
                      </Button>
                      <Tooltip title="Copy webhook token">
                        <IconButton size="small" disabled={busy} onClick={() => copyToken(w)}>
                          <VpnKeyOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Button size="small" variant="outlined" disabled={busy} onClick={() => rotate(w)}>
                        Xoay token
                      </Button>
                      <Button size="small" variant="outlined" disabled={busy} onClick={() => toggle(w)}>
                        {w.enabled ? 'Tắt' : 'Bật'}
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};
